import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Card, CardContent, Typography, Box } from '@material-ui/core';
import { getThemeColors } from '../utils/themeUtils';
import * as MarketplaceActions from 'app/main/marketplace/store/actions';

const useStyles = makeStyles(theme => ({
	appsGrid: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		gap: theme.spacing(3),
		position: 'relative'
	},
	appCardWrapper: {
		position: 'relative',
		minHeight: '160px',
		'&:nth-child(2)': {
			'@media (max-width: 1600px)': {
				display: 'none'
			}
		}
	},
	appCard: {
		borderRadius: '12px',
		cursor: 'pointer',
		overflow: 'hidden',
		minHeight: '160px',
		position: 'absolute',
		width: '100%',
		top: 0,
		left: 0,
		transition: 'opacity 0.6s ease-in-out, transform 0.6s ease-in-out',
		'&:hover': {
			transform: 'translateX(4px) !important'
		}
	},
	appCardVisible: {
		opacity: 1,
		transform: 'translateX(0)',
		zIndex: 2
	},
	appCardHidden: {
		opacity: 0,
		transform: 'translateX(20px)',
		zIndex: 1,
		pointerEvents: 'none'
	},
	cardContent: {
		padding: theme.spacing(2.5),
		paddingRight: '150px',
		display: 'flex',
		flexDirection: 'row',
		gap: theme.spacing(2),
		alignItems: 'flex-start',
		position: 'relative',
		'&:last-child': {
			paddingBottom: theme.spacing(2.5)
		}
	},
	appLogo: {
		width: '72px',
		height: '72px',
		minWidth: '72px',
		minHeight: '72px',
		maxWidth: '72px',
		maxHeight: '72px',
		borderRadius: '12px',
		objectFit: 'contain',
		flexShrink: 0,
		padding: '10px'
	},
	appContent: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.75)
	},
	appName: {
		fontSize: '15px',
		fontWeight: 600,
		lineHeight: 1.3
	},
	appDescription: {
		fontSize: '12px',
		lineHeight: 1.6
	},
	screenshotContainer: {
		position: 'absolute',
		right: '10px',
		top: '50%',
		transform: 'translateY(-50%)',
		width: '130px',
		height: '110px',
		perspective: '600px',
		pointerEvents: 'none'
	},
	screenshot: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
		borderRadius: '6px',
		transform: 'rotateY(-25deg) rotateX(3deg)',
		transformStyle: 'preserve-3d',
		transformOrigin: 'left center'
	},
	screenshotDark: {
		boxShadow: '0 8px 20px rgba(0, 0, 0, 0.5), -4px 0 12px rgba(0, 0, 0, 0.3)',
		border: '1px solid rgba(255, 255, 255, 0.15)'
	},
	screenshotLight: {
		boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
		border: '1px solid rgba(0, 0, 0, 0.08)'
	}
}));

// Helper to assign colors based on app name
const getAppColor = (name) => {
	const colorMap = {
		'E4S': '#22c55e',
		'WatsonX': '#3b82f6',
		'Watsonx': '#3b82f6',
		'IBM Watsonx': '#3b82f6',
		'Heroic': '#f97316',
		'ClassifyX': '#8b5cf6'
	};
	
	for (const key in colorMap) {
		if (name.includes(key)) return colorMap[key];
	}
	return '#64748b'; // Default color
};

const FeaturedAppsCards = ({ themeMode = 'dark' }) => {
	const classes = useStyles();
	const colors = getThemeColors(themeMode);
	const isDark = themeMode === 'dark';
	const [currentIndex, setCurrentIndex] = useState(0);
	const [screenshotIndex, setScreenshotIndex] = useState({});
	const [featuredApps, setFeaturedApps] = useState([]);
	const history = useHistory();
	const dispatch = useDispatch();
	
	// Get user session and clusters from Redux (needed for app launching)
	const userSession = useSelector(({ auth }) => auth.user);
	const clusters = useSelector(({ clusters }) => clusters.cluster.list);
	
	// Get marketplace data from Redux store (loaded via authenticated API)
	const marketplaceData = useSelector(state =>
		state.marketplace && state.marketplace.list
			? state.marketplace.list
			: []
	);
	
	// Load marketplace data if not already in store, then extract featured apps
	useEffect(() => {
		if (!marketplaceData || marketplaceData.length === 0) {
			dispatch(MarketplaceActions.getMarketplaceStacks());
		}
	}, [dispatch]);
	
	useEffect(() => {
		if (!marketplaceData || marketplaceData.length === 0) return;
		
		const featured = marketplaceData.filter(app => app.featured === true);
		
		const formattedApps = featured.map(app => ({
			id: app.id,
			name: app.name,
			description: app.description.substring(0, 180) + (app.description.length > 180 ? '...' : ''),
			logo: `/assets/images/logos/${app.logo}`,
			color: getAppColor(app.name),
			screenshots: app.screenshots || [],
			// Store full app data for launching
			fullData: app
		}));
		
		setFeaturedApps(formattedApps);
	}, [marketplaceData]);

	// Auto-rotate through apps every 16 seconds
	useEffect(() => {
		if (featuredApps.length === 0) return;
		
		const interval = setInterval(() => {
			setCurrentIndex((prevIndex) => (prevIndex + 2) % featuredApps.length);
		}, 16000); // Rotate every 16 seconds

		return () => clearInterval(interval);
	}, [featuredApps.length]);
	
	// Rotate screenshots every 5 seconds for each app
	useEffect(() => {
		if (featuredApps.length === 0) return;
		
		const interval = setInterval(() => {
			setScreenshotIndex((prev) => {
				const newIndex = {};
				featuredApps.forEach(app => {
					if (app.screenshots && app.screenshots.length > 0) {
						const currentIdx = prev[app.id] || 0;
						newIndex[app.id] = (currentIdx + 1) % app.screenshots.length;
					}
				});
				return newIndex;
			});
		}, 5000); // Rotate screenshots every 5 seconds

		return () => clearInterval(interval);
	}, [featuredApps]);

	const handleAppClick = async (app) => {
		if (!app || !app.fullData) {
			console.warn('No app data available');
			history.push('/heidi-apps');
			return;
		}

		const appData = app.fullData;
		
		if (appData.action === 'Launch') {
			// Handle web platform apps
			if (appData.platform === 'web') {
				if (appData.config?.web_app_url) {
					let launchUrl = appData.config.web_app_url;
					
					// Add SSO token for partner apps that have SSO enabled
					if (appData.config?.sso_enabled && userSession) {
						try {
							const ssoToken = await dispatch(MarketplaceActions.generateMarketplaceSSO(appData.config));
							const separator = launchUrl.includes('?') ? '&' : '?';
							launchUrl = `${launchUrl}${separator}heidi_token=${ssoToken}`;
						} catch (error) {
							console.error('Failed to generate SSO token:', error);
						}
					}
					
					window.open(launchUrl, '_blank');
				}
				return;
			}
			
			// Handle cluster-app platform
			if (appData.platform === 'cluster-app') {
				let userAvailableClusters = clusters?.filter(cluster => {
					return cluster.info?.state === 'available' && 
						   cluster.user?.includes(userSession?.username);
				});
				
				if (!userAvailableClusters || userAvailableClusters.length === 0) {
					console.warn('No available clusters found - redirecting to heidi-apps');
					history.push('/heidi-apps');
					return;
				}
				
				// If only one cluster available, launch directly
				if (userAvailableClusters.length === 1) {
					const cluster = userAvailableClusters[0];
					let clusterCommand = appData.config?.['cluster-command'] || '';
					
					// Generate SSO token if needed
					if (appData.config?.sso_enabled && userSession && clusterCommand) {
						try {
							const ssoToken = await dispatch(MarketplaceActions.generateMarketplaceSSO(appData.config));
							const fullUrl = `${appData.config.web_app_url}?heidi_token=${ssoToken}`;
							const encodedUrl = btoa(fullUrl);
							clusterCommand = clusterCommand.replace('WEB_APP_URL_WITH_SSO_TOKEN', encodedUrl);
						} catch (error) {
							console.error('Failed to generate SSO token:', error);
						}
					}
					
					// Navigate to cluster access tab with VNC auto-launch
					const searchParams = new URLSearchParams({
						autoLaunchVNC: 'true',
						clusterApp: 'true',
						clusterCommand: encodeURIComponent(clusterCommand)
					});
					
					const url = `/clusters/${cluster.id}?${searchParams.toString()}#access`;
					history.push(url);
					return;
				} else {
					history.push('/heidi-apps');
					return;
				}
			}
			
			// Handle AWS platform
			if (appData.platform === 'aws') {
				// If app has supportedProviders, go to cluster creation with provider picker
				if (appData.supportedProviders && appData.supportedProviders.length > 0) {
					dispatch(MarketplaceActions.setStack(appData));
					history.push('/clusters');
					return;
				}
				history.push('/heidi-apps');
				return;
			}
			
			// For other cloud platforms (gcp, azure, ibm, oracle) - or any platform with supportedProviders
			if (appData.supportedProviders && appData.supportedProviders.length > 0) {
				dispatch(MarketplaceActions.setStack(appData));
				history.push('/clusters');
				return;
			}

			dispatch(MarketplaceActions.setStack(appData));
			history.push('/clusters');
		} else {
			// App doesn't have Launch action - navigate to apps page
			history.push('/heidi-apps');
		}
	};
	
	// Don't render until featuredApps are loaded
	if (featuredApps.length === 0) {
		return null;
	}

	// Get current and next sets of apps for smooth crossfade
	const getCurrentApps = () => {
		const apps = [];
		for (let i = 0; i < 2; i++) {
			const index = (currentIndex + i) % featuredApps.length;
			apps.push({ ...featuredApps[index], key: `${featuredApps[index].id}-${currentIndex}` });
		}
		return apps;
	};

	const currentApps = getCurrentApps();

	// Render app card
	const renderAppCard = (app, slotIndex, isVisible) => (
		<Card 
			key={app.key}
			className={`${classes.appCard} ${isVisible ? classes.appCardVisible : classes.appCardHidden}`}
			onClick={() => handleAppClick(app)}
			style={{
				backgroundColor: colors.card.background,
				border: `1px solid ${colors.card.border}`,
				boxShadow: colors.card.shadow
			}}
		>
			<CardContent className={classes.cardContent}>
				{/* Icon on the left */}
				<Box
					component="img"
					src={app.logo}
					alt={app.name}
					className={classes.appLogo}
					style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
					onError={(e) => {
						// Fallback to colored circle with initial
						e.target.style.display = 'none';
						const fallback = document.createElement('div');
						fallback.style.cssText = `width: 72px; height: 72px; border-radius: 12px; background-color: ${app.color}; display: flex; align-items: center; justify-content: center; font-size: 28px; font-weight: 700; color: #fff; flex-shrink: 0;`;
						fallback.textContent = app.name.charAt(0);
						e.target.parentNode.insertBefore(fallback, e.target);
					}}
				/>
				{/* Text content on the right */}
				<div className={classes.appContent}>
					<Typography 
						className={classes.appName}
						style={{ color: colors.text.primary }}
					>
						{app.name}
					</Typography>
					<Typography 
						className={classes.appDescription}
						style={{ color: colors.text.muted }}
					>
						{app.description}
					</Typography>
				</div>
				{/* Screenshot preview with perspective - rotating through available screenshots */}
				{app.screenshots && app.screenshots.length > 0 && (
					<Box className={classes.screenshotContainer}>
						<Box
							component="img"
							src={app.screenshots[screenshotIndex[app.id] || 0]}
							alt={`${app.name} screenshot`}
							className={`${classes.screenshot} ${isDark ? classes.screenshotDark : classes.screenshotLight}`}

						/>
					</Box>
				)}
				{(!app.screenshots || app.screenshots.length === 0) && (
					<Box style={{ position: 'absolute', right: '10px', bottom: '10px', fontSize: '10px', color: 'red' }}>
						No screenshots
					</Box>
				)}
			</CardContent>
		</Card>
	);

	return (
		<div className={classes.appsGrid}>
			{[0, 1].map((slotIndex) => (
				<div key={slotIndex} className={classes.appCardWrapper}>
					{featuredApps.map((app, appIndex) => {
						// Check if this app should be visible in this slot
						const isVisible = (currentIndex + slotIndex) % featuredApps.length === appIndex;
						return renderAppCard(
							{ ...app, key: `${app.id}-${appIndex}-${slotIndex}` },
							slotIndex,
							isVisible
						);
					})}
				</div>
			))}
		</div>
	);
};

export default React.memo(FeaturedAppsCards, (prevProps, nextProps) => {
	return prevProps.themeMode === nextProps.themeMode;
});
